import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { Box, CircularProgress, Grid, Menu, MenuItem, Typography } from '@mui/material';
import { motion } from 'framer-motion';
import { get, sortBy } from 'lodash';
import rehypeHighlight from 'rehype-highlight';
import rehypeRaw from 'rehype-raw';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';

import {
  trackClickRegenerate,
  trackClickVideoGuide,
} from '../utils/analytics/analytics';
import { handleApiError as handleApiErrorUtil, regenerateItem } from '../utils/api';
import { colors } from '../utils/colors';
import {
  ANIMATION_DURATION_MS,
  SECTIONS_NAME_DESCRIPTION,
  getTabGenerationCost,
} from '../utils/const';
import { formatCustomDistanceToNow } from '../utils/dateUtils';
import { useFirebaseLoginState } from '../utils/hooks';
import { RootState } from '../utils/store';
import { postProcessSummary as postProcessMarkdown } from '../utils/textUtils';
import { Item, LoadingStatus, RequestType, SectionName, TabName } from '../utils/types';
import { SMMStructuredView } from './SMMStructuredView';
import { InfoTooltipView } from './minicomponents/InfoTooltipView';
import {
  IconError,
  IconKeyboardArrowDown,
  IconNotes,
  IconReload,
  IconVideoGuide,
} from './minicomponents/icons';
import {
  ButtonWithIcon,
  CopyButton,
  ReactMarkdownStyled,
} from './minicomponents/minicomponents';
import AlertPopup from './popups/AlertPopup';
import LoadingPopup from './popups/LoadingPopup';
import RegeneratePopup from './popups/RegeneratePopup';
import UpgradePopup from './popups/UpgradePopup';
import { GradientButton } from './minicomponents/GradientButton';

interface CampaignItemSectionComponentProps {
  item: Item;
  sectionName: SectionName;
  tabName: TabName | null;
  isReadOnly?: boolean;
}

export const CampaignSectionItem = React.memo(
  ({
    item,
    sectionName,
    tabName,
    isReadOnly = false,
  }: CampaignItemSectionComponentProps) => {
    const { t } = useTranslation();
    const { userId } = useFirebaseLoginState();
    const state = useSelector((state: RootState) => state.appState);
    const creditsLeft = state?.subcription?.credits || 0;
    const selectedCampaignId = useSelector(
      (state: RootState) => state.appState.selectedCampaign
    )?.id;

    // get title, description, and visibility flags moved here from CampaignSection.tsx
    const itemInfo = get(SECTIONS_NAME_DESCRIPTION, item.type) as {
      name?: string;
      description?: string;
      hideTitle?: boolean;
      hideDescription?: boolean;
      videoURL?: string;
    };

    const title = t(itemInfo?.name || item.type);
    const description = itemInfo?.description ? t(itemInfo.description) : undefined;
    const hideTitle = (itemInfo?.name && itemInfo.hideTitle) || isReadOnly;
    const hideDescription = itemInfo?.name && itemInfo.hideDescription;
    const videoURL = itemInfo?.videoURL;

    const isStatusLoading = item?.status === LoadingStatus.LOADING;
    const isStatusCompleted = item?.status === LoadingStatus.COMPLETED;
    const isStatusFailed = item?.status === LoadingStatus.FAILED;

    const versionsObject = item?.versions || [];
    const sortedVersions = useMemo(
      () => sortBy(versionsObject, (v) => v.created_at).reverse(),
      [versionsObject]
    );

    const [activeVersionIndex, setActiveVersionIndex] = useState(0);
    const activeVersion = sortedVersions[activeVersionIndex];

    const markdownClean = postProcessMarkdown(activeVersion?.text || '');

    const totalVersions = sortedVersions.length;
    const hasMultipleVersions = totalVersions > 1;

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const menuOpen = Boolean(anchorEl);

    const [regeneratePopupOpen, setRegeneratePopupOpen] = useState(false);
    const [isRegenerating, setIsRegenerating] = useState(false);
    const [regenerateError, setRegenerateError] = useState<string | null>(null);
    const [upgradePopupOpen, setUpgradePopupOpen] = useState(false);

    const handleError = useCallback((error: unknown) => {
      handleApiErrorUtil({
        error,
        onSetUpgradePopupOpen: setUpgradePopupOpen,
        onSetErrorMessage: setRegenerateError,
        onSetErrorAlertOpen: (isOpen) => {
          if (!isOpen) setRegenerateError(null);
        },
      });
    }, []);

    const handleRegenerateSubmit = async (prompt: string) => {
      if (!userId || !selectedCampaignId || !item) return;
      setIsRegenerating(true);
      setRegenerateError(null);
      try {
        await regenerateItem(
          userId,
          selectedCampaignId,
          item.id!,
          activeVersion?.version || 0,
          state.creativity,
          state.session,
          prompt
        );
      } catch (error) {
        handleError(error);
      } finally {
        setIsRegenerating(false);
        setRegeneratePopupOpen(false);
      }
    };

    const generationCost = getTabGenerationCost(tabName, sectionName);

    const handleRegenerateButtonClick = () => {
      // Track regenerate click analytics
      if (sectionName && tabName) {
        const itemName = item.type;
        trackClickRegenerate(sectionName, tabName, itemName, sortedVersions.length);
      }

      if (!isStatusFailed) {
        if (creditsLeft < generationCost) {
          setUpgradePopupOpen(true);
          return;
        }
      }
      if (isStatusFailed && userId && selectedCampaignId) {
        handleRegenerateSubmit(activeVersion?.prompt || '');
      } else {
        setRegeneratePopupOpen(true);
      }
    };

    const handlePreviousVersion = () => {
      if (activeVersionIndex > 0) setActiveVersionIndex(activeVersionIndex - 1);
    };
    const handleNextVersion = () => {
      if (activeVersionIndex < totalVersions - 1)
        setActiveVersionIndex(activeVersionIndex + 1);
    };
    const handleVersionMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) =>
      setAnchorEl(event.currentTarget);
    const handleVersionMenuClose = () => setAnchorEl(null);
    const handleVersionSelect = (index: number) => {
      setActiveVersionIndex(index);
      handleVersionMenuClose();
    };
    const handleNotes = () => {};

    // The entire return is now wrapped to include the title/description,
    // and then it conditionally renders the correct item body.
    return (
      <Grid size={12} container gap={2}>
        {/* Title/Description Wrapper, moved from CampaignSection.tsx */}
        <Grid size={12}>
          {!hideTitle && title && <Typography variant="h2">{title}</Typography>}
          {!hideDescription && description && (
            <Box sx={{ display: 'flex', alignItems: 'top', gap: 2 }}>
              <Typography variant="body1" sx={{ flexGrow: 1 }}>
                {description}
              </Typography>
              {videoURL && (
                <GradientButton
                  version="text"
                  component="a"
                  href={videoURL}
                  target="_blank"
                  rel="noopener noreferrer"
                  iconLeft={<IconVideoGuide />}
                  // iconLeft={<IconVideoGuide sx={{ color: '#FF0033' }} />} // optional YouTube red icon
                  buttonText={t('app.buttons.checkVideoGuide')}
                  onClick={() => {
                    trackClickVideoGuide(sectionName, tabName);
                  }}
                  sx={{
                    flexShrink: 0,
                    whiteSpace: 'nowrap',
                    minWidth: 'auto',
                    position: 'relative',
                    top: '-10px',
                  }}
                />
              )}
            </Box>
          )}
        </Grid>

        {/* Conditional rendering for the item body */}
        {item.type === RequestType.STRUCTURED_GROUP_SMM_POST ? (
          // Path for the new structured item type
          <SMMStructuredView
            parentItem={item}
            sectionName={sectionName}
            tabName={tabName || undefined}
          />
        ) : (
          // This is the ORIGINAL JSX for rendering simple items, now inside an 'else' block.
          <Grid
            container
            size={12}
            sx={{ borderRadius: '6px', bgcolor: colors.custom.bgGrey }}
          >
            {(isStatusCompleted || isStatusFailed) && (
              <Grid
                container
                size={12}
                sx={{
                  borderTopLeftRadius: '6px',
                  borderTopRightRadius: '6px',
                  bgcolor: colors.primary.light,
                  alignItems: 'center',
                  px: 2,
                  height: 32,
                }}
              >
                <Grid
                  size={'grow'}
                  container
                  justifyContent="flex-end"
                  alignItems="center"
                >
                  {hasMultipleVersions && (
                    <>
                      <ButtonWithIcon
                        icon={<IconKeyboardArrowDown sx={{ rotate: '90deg' }} />}
                        isAccentOnHover={false}
                        onClick={handleNextVersion}
                        isDisabled={activeVersionIndex === totalVersions - 1}
                      />
                      <ButtonWithIcon
                        onClick={handleVersionMenuOpen}
                        icon={
                          <Typography
                            sx={{
                              cursor: 'pointer',
                              '&:hover': { color: 'primary.main' },
                            }}
                          >
                            {t('app.campaign.version', {
                              current: totalVersions - activeVersionIndex,
                              total: totalVersions,
                            })}
                          </Typography>
                        }
                        sx={{ minWidth: 90 }}
                        isAccentOnHover={true}
                      />
                      <Menu
                        id="version-menu"
                        anchorEl={anchorEl}
                        open={menuOpen}
                        onClose={handleVersionMenuClose}
                      >
                        {sortedVersions.map((version, index) => (
                          <MenuItem
                            key={version.version}
                            onClick={() => handleVersionSelect(index)}
                          >
                            <Typography
                              fontWeight={
                                index === activeVersionIndex ? 'bold' : 'normal'
                              }
                            >
                              {totalVersions - index}){' '}
                              {formatCustomDistanceToNow(version.created_at!)}
                            </Typography>
                          </MenuItem>
                        ))}
                      </Menu>
                      <InfoTooltipView
                        text={activeVersion?.prompt || ''}
                        icon={
                          <ButtonWithIcon
                            icon={<IconNotes />}
                            onClick={handleNotes}
                            isAccentOnHover={false}
                            isDisabled={!activeVersion?.prompt}
                          />
                        }
                        isDisabled={!activeVersion?.prompt}
                      />
                      <ButtonWithIcon
                        icon={<IconKeyboardArrowDown sx={{ rotate: '270deg' }} />}
                        isAccentOnHover={false}
                        onClick={handlePreviousVersion}
                        isDisabled={activeVersionIndex === 0}
                      />
                    </>
                  )}
                  {isStatusCompleted && (
                    <InfoTooltipView
                      icon={
                        <CopyButton
                          textToCopy={markdownClean}
                          sectionName={sectionName}
                          tabName={tabName || undefined}
                          itemName={item.type}
                        />
                      }
                      text={t('app.popups.copy.tooltip', { title })}
                    />
                  )}
                  {!isReadOnly && (
                    <InfoTooltipView
                      icon={
                        <ButtonWithIcon
                          icon={<IconReload />}
                          onClick={handleRegenerateButtonClick}
                        />
                      }
                      text={
                        isStatusFailed
                          ? t('app.popups.regenerate.recoverText')
                          : t('app.popups.regenerate.customizeText', { title })
                      }
                    />
                  )}
                </Grid>
              </Grid>
            )}
            <Grid
              size={12}
              sx={{
                px: 3,
                pb: 2,
                color: colors.primary.dark,
                textAlign: !isStatusCompleted ? 'center' : 'left',
                mt: isStatusLoading ? 2 : 0,
              }}
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: ANIMATION_DURATION_MS / 1000,
                  ease: 'easeOut',
                }}
              >
                {isStatusLoading && (
                  <CircularProgress size={24} sx={{ color: 'text.secondary' }} />
                )}
                {isStatusFailed && (
                  <Box pt={2}>
                    <IconError sx={{ color: 'error.main', width: 24, height: 24 }} />
                    <Typography>{t('app.errors.failedToGenerate')}</Typography>
                    <Typography color="text.secondary">
                      {t('app.errors.noChargeForFailedGeneration')}
                    </Typography>
                  </Box>
                )}
                {isStatusCompleted && (
                  <ReactMarkdownStyled
                    remarkPlugins={[remarkGfm, remarkBreaks]}
                    rehypePlugins={[rehypeRaw, rehypeHighlight]}
                  >
                    {markdownClean}
                  </ReactMarkdownStyled>
                )}
              </motion.div>
            </Grid>
            <RegeneratePopup
              open={regeneratePopupOpen}
              onClose={() => setRegeneratePopupOpen(false)}
              onSubmit={handleRegenerateSubmit}
              title={t('app.popups.regenerate.title', { title })}
              descriptionKey={itemInfo?.description}
              buttonText={t('app.popups.regenerate.buttonText', {
                count: generationCost,
              })}
              generationCost={generationCost}
              creditsLeft={creditsLeft}
              isRemarksMandatory={false}
              sectionName={sectionName}
              tabName={tabName || undefined}
              itemName={item.type}
              creativityLevel={state.creativity}
              regenerationsCount={sortedVersions.length}
            />
            <LoadingPopup
              open={isRegenerating}
              title={t('app.popups.loading.regeneratingTitle', { title })}
              initialText={t('app.popups.loading.craftingNewVersion', { title })}
            />
            <AlertPopup
              open={!!regenerateError}
              onClose={() => setRegenerateError(null)}
              onSubmit={() => setRegenerateError(null)}
              buttonText={t('app.buttons.ok')}
              alertText={regenerateError || ''}
              title={t('app.popups.error.title')}
            />
            <UpgradePopup
              open={upgradePopupOpen}
              onClose={() => setUpgradePopupOpen(false)}
              onError={handleError}
            />
          </Grid>
        )}
      </Grid>
    );
  },
  (prevProps, nextProps) =>
    JSON.stringify(prevProps.item) === JSON.stringify(nextProps.item) &&
    prevProps.sectionName === nextProps.sectionName &&
    prevProps.tabName === nextProps.tabName
);
